import { LabelWithAction } from "@/components/form/labels/LabelWithAction";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { AntIconType } from "@/components/icons/AntIcon";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Col, Descriptions, DescriptionsProps, Row, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { CommissionsBatchStep } from "../enums";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../paths";
import { CommissionsBatch } from "../types";
import { isCommissionAdvanceBatch } from "../utils";

type SplitAmounts = [ratio30?: number, firstRatio10?: number, secondRatio10?: number, otherRatios?: number];

interface SplitAmountsLabelProps {
  label: string;
  title: string;
  amounts: SplitAmounts;
}

const SplitAmountsLabel = ({ label, title, amounts }: SplitAmountsLabelProps) => {
  const { t } = useTranslation();

  return (
    <LabelWithPopover
      label={label}
      popoverContent={
        <Descriptions
          style={{ width: 380 }}
          styles={{ content: { textAlign: "right" } }}
          size="small"
          bordered
          title={title}
          column={1}
          items={[
            "splitAmountRatio30",
            "splitAmountFirstRatio10",
            "splitAmountSecondRatio10",
            "splitAmountOtherRatios",
          ].map((labelKey, index) => ({
            key: labelKey,
            label: t(`commissions.batch.attrs.${labelKey}`),
            children: formatLocaleCurrency(amounts[index] ?? 0),
          }))}
        />
      }
    />
  );
};

const sumSplitAmounts = (amounts: SplitAmounts) =>
  amounts.reduce<number>((sum, amount) => sum + numberOrZero(amount), 0);

interface Props {
  batch: CommissionsBatch;
}

export const CommissionsBatchAmounts = ({ batch }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const isAdvanceBatch = isCommissionAdvanceBatch(batch.type, isTopAgentPlus);
  const hasAdvanceAmounts = isDefined(batch.advanceCommissionAmount) || isDefined(batch.advancePayoutCommissionAmount);

  const isOutputStep = [
    CommissionsBatchStep.RECAPITULATION,
    CommissionsBatchStep.REVIEW,
    CommissionsBatchStep.FINISH,
  ].includes(batch.step);

  const previousSplitAmounts: SplitAmounts = [
    batch.totalPreviousSplitAmount30,
    batch.totalPreviousSplitAmountFirst10,
    batch.totalPreviousSplitAmountSecond10,
    batch.totalPreviousSplitAmountOthers,
  ];

  const structurePreviousSplitAmounts: SplitAmounts = [
    batch.structurePreviousSplitAmount30,
    batch.structurePreviousSplitAmountFirst10,
    batch.structurePreviousSplitAmountSecond10,
    batch.structurePreviousSplitAmountOthers,
  ];

  const currentSplitAmounts: SplitAmounts = [
    batch.totalSplitAmount30,
    batch.totalSplitAmountFirst10,
    batch.totalSplitAmountSecond10,
    batch.totalSplitAmountOthers,
  ];

  const structureCurrentSplitAmounts: SplitAmounts = [
    batch.structureSplitAmount30,
    batch.structureSplitAmountFirst10,
    batch.structureSplitAmountSecond10,
    batch.structureSplitAmountOthers,
  ];

  const areMovementsManageable = batch.step === CommissionsBatchStep.RECAPITULATION;
  const movementsActionText = areMovementsManageable ? t("common.manage") : t("common.show");
  const movementsActionIcon: AntIconType = areMovementsManageable ? "interaction" : "eye";

  const groups: {
    key: string;
    title: string;
    items: NonNullable<DescriptionsProps["items"]>;
  }[] = [
    {
      key: "input",
      title: t("commissions.batch.sections.inputAmounts"),
      items: [
        {
          key: "allCommissionAmount",
          label: <Typography.Text strong>{t("commissions.batch.attrs.allCommissionAmount")}</Typography.Text>,
          children: <Typography.Text strong>{formatLocaleCurrency(batch.allCommissionAmount ?? 0)}</Typography.Text>,
        },
        {
          key: "currentCommissionAmount",
          label: t("commissions.batch.attrs.currentCommissionAmount"),
          children: formatLocaleCurrency(batch.currentCommissionAmount ?? 0),
        },
        {
          key: "previousCommissionAmount",
          label:
            batch.step === CommissionsBatchStep.IMPORT ? (
              <LabelWithAction
                label={t("commissions.batch.attrs.previousCommissionAmount")}
                actionText={t("common.manage")}
                icon="interaction"
                path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.postponedCommissions.to, { id: batch.id })}
                disabled={batch.stepChangeInProgress}
              />
            ) : (
              t("commissions.batch.attrs.previousCommissionAmount")
            ),
          children: formatLocaleCurrency(batch.previousCommissionAmount ?? 0),
        },
        isTopAgentPlus && {
          key: "previousSplitAmount",
          label: (
            <SplitAmountsLabel
              label={t("commissions.batch.helpers.previousSplitAmount")}
              title={t("commissions.batch.helpers.previousSplitTitle")}
              amounts={previousSplitAmounts}
            />
          ),
          children: formatLocaleCurrency(sumSplitAmounts(previousSplitAmounts)),
        },
        isTopAgentPlus && {
          key: "structurePreviousSplitAmount",
          label: (
            <SplitAmountsLabel
              label={t("commissions.batch.helpers.structurePreviousSplitAmount")}
              title={t("commissions.batch.helpers.structurePreviousSplitTitle")}
              amounts={structurePreviousSplitAmounts}
            />
          ),
          children: formatLocaleCurrency(sumSplitAmounts(structurePreviousSplitAmounts)),
        },
      ].filter((item) => !!item),
    },
    {
      key: "calculation",
      title: t("commissions.batch.sections.calculationAmounts"),
      items: [
        {
          key: "calculatedCommissionAmount",
          label: t("commissions.batch.attrs.calculatedCommissionAmount"),
          children: formatLocaleCurrency(batch.calculatedCommissionAmount ?? 0),
        },
        {
          key: "postponedCommissionAmount",
          label: t("commissions.batch.attrs.postponedCommissionAmount"),
          children: formatLocaleCurrency(batch.postponedCommissionAmount ?? 0),
        },
        isTopAgentPlus && {
          key: "currentSplitAmount",
          label: (
            <SplitAmountsLabel
              label={t("commissions.batch.helpers.currentSplitAmount")}
              title={t("commissions.batch.helpers.currentSplitTitle")}
              amounts={currentSplitAmounts}
            />
          ),
          children: formatLocaleCurrency(sumSplitAmounts(currentSplitAmounts)),
        },
        isTopAgentPlus && {
          key: "structureCurrentSplitAmount",
          label: (
            <SplitAmountsLabel
              label={t("commissions.batch.helpers.structureCurrentSplitAmount")}
              title={t("commissions.batch.helpers.structureCurrentSplitTitle")}
              amounts={structureCurrentSplitAmounts}
            />
          ),
          children: formatLocaleCurrency(sumSplitAmounts(structureCurrentSplitAmounts)),
        },
      ].filter((item) => !!item),
    },
    {
      key: "source",
      title: t("commissions.batch.sections.sourceAmounts"),
      items: [
        {
          key: "bankAccountCommissionAmount",
          label: t("commissions.batch.attrs.bankAccountCommissionAmount"),
          children: formatLocaleCurrency(batch.bankAccountCommissionAmount ?? 0),
        },
        {
          key: "invoiceCommissionAmount",
          label: t("commissions.batch.attrs.invoiceCommissionAmount"),
          children: formatLocaleCurrency(batch.invoiceCommissionAmount ?? 0),
        },
        {
          key: "volumeCommissionAmount",
          label: t("commissions.batch.attrs.volumeCommissionAmount"),
          children: formatLocaleCurrency(batch.volumeCommissionAmount ?? 0),
        },
      ],
    },
    {
      key: "manualMovements",
      title: t("commissions.batch.sections.manualMovements"),
      items: [
        {
          key: "manualBailAccountMovementsCount",
          label: isOutputStep ? (
            <LabelWithAction
              label={t("commissions.batch.attrs.manualBailAccountMovementsCount")}
              actionText={movementsActionText}
              icon={movementsActionIcon}
              path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.manualBailAccountMovements.to, { id: batch.id })}
              disabled={batch.stepChangeInProgress}
            />
          ) : (
            t("commissions.batch.attrs.manualBailAccountMovementsCount")
          ),
          children: batch.manualBailAccountMovementsCount,
        },
        {
          key: "manualDebtAccountMovementsCount",
          label: isOutputStep ? (
            <LabelWithAction
              label={t("commissions.batch.attrs.manualDebtAccountMovementsCount")}
              actionText={movementsActionText}
              icon={movementsActionIcon}
              path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.manualDebtAccountMovements.to, { id: batch.id })}
              disabled={batch.stepChangeInProgress}
            />
          ) : (
            t("commissions.batch.attrs.manualDebtAccountMovementsCount")
          ),
          children: batch.manualDebtAccountMovementsCount,
        },
      ],
    },
    isAdvanceBatch &&
      hasAdvanceAmounts && {
        key: "advance",
        title: t("commissions.batch.sections.advanceAmounts"),
        items: [
          {
            key: "advanceCommissionAmount",
            label: (
              <LabelWithTooltip
                label={t("commissions.batch.attrs.advanceCommissionAmount")}
                tooltip={t("commissions.batch.helpers.advanceCommissionAmountDesc")}
              />
            ),
            children: formatLocaleCurrency(batch.advanceCommissionAmount ?? 0),
          },
          {
            key: "advancePayoutCommissionAmount",
            label: (
              <LabelWithTooltip
                label={t("commissions.batch.attrs.advancePayoutCommissionAmount")}
                tooltip={t("commissions.batch.helpers.advancePayoutCommissionAmountDesc")}
              />
            ),
            children: formatLocaleCurrency(batch.advancePayoutCommissionAmount ?? 0),
          },
        ],
      },
  ].filter((group) => !!group);

  return (
    <Row gutter={[24, 24]}>
      {groups.map((group) => (
        <Col key={group.key} xs={24} lg={12} xl={8}>
          <Descriptions
            title={group.title}
            items={group.items}
            bordered
            size="small"
            column={1}
            styles={{
              title: { fontSize: 14 },
              header: { marginBottom: 8 },
              content: { width: 140, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
            }}
          />
        </Col>
      ))}
    </Row>
  );
};
